import React, { useState } from 'react';
import { ChevronRight, Terminal } from 'lucide-react';
import AnimatedGradientText from './AnimatedGradientText';
import { useLang } from './LanguageProvider';
import translations from '../translations';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const { lang } = useLang();
  const t = translations[lang].faq;

  return (
    <section data-testid="faq-section" id="faq" className="py-20 sm:py-24 lg:py-28 relative">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-emerald-500/10 bg-emerald-50 border dark:border-emerald-500/20 border-emerald-200 mb-5">
            <Terminal className="w-3 h-3 text-emerald-500" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-500">{t.badge}</span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            <AnimatedGradientText>{t.title}</AnimatedGradientText>
          </h2>
          <p className="text-base dark:text-white/50 text-slate-500">{t.desc}</p>
        </div>

        {/* Terminal-style FAQ */}
        <div
          data-testid="faq-terminal"
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(2, 6, 23, 0.92)',
            border: '1px solid rgba(16, 185, 129, 0.15)',
            boxShadow: 'rgba(16, 185, 129, 0.08) 0px 0px 0px 1px, rgba(0, 0, 0, 0.5) 0px 30px 80px',
          }}
        >
          {/* Terminal header with hover symbols on dots */}
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
            <div className="terminal-dot-group flex items-center gap-2">
              <div className="terminal-dot terminal-dot-close group relative">
                <div className="h-3 w-3 rounded-full bg-red-500/80 flex items-center justify-center cursor-default">
                  <span className="terminal-dot-symbol text-red-900 text-[8px] font-bold opacity-0 group-hover:opacity-100 transition-opacity leading-none">&#10005;</span>
                </div>
              </div>
              <div className="terminal-dot terminal-dot-minimize group relative">
                <div className="h-3 w-3 rounded-full bg-yellow-500/80 flex items-center justify-center cursor-default">
                  <span className="terminal-dot-symbol text-yellow-900 text-[8px] font-bold opacity-0 group-hover:opacity-100 transition-opacity leading-none">&#8722;</span>
                </div>
              </div>
              <div className="terminal-dot terminal-dot-expand group relative">
                <div className="h-3 w-3 rounded-full bg-green-500/80 flex items-center justify-center cursor-default">
                  <span className="terminal-dot-symbol text-green-900 text-[8px] font-bold opacity-0 group-hover:opacity-100 transition-opacity leading-none">&#43;</span>
                </div>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center gap-1.5">
              <Terminal className="w-3 h-3 text-emerald-400/60" />
              <span className="font-mono text-[10px] text-emerald-300/50">{t.terminal}</span>
              <span className="terminal-cursor inline-block w-[6px] h-[14px] bg-emerald-400/70 ml-0.5" />
            </div>
          </div>

          {/* FAQ items */}
          <div className="divide-y divide-white/5">
            {t.items.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-white/[0.03] transition-colors bg-transparent border-none cursor-pointer group"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <ChevronRight
                    className={`w-4 h-4 text-emerald-400/80 shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-90' : ''}`}
                  />
                  <span className="font-mono text-sm text-slate-200 group-hover:text-white transition-colors">
                    <span className="text-emerald-400/70">$ </span>{faq.q}
                  </span>
                </button>
                {openIndex === i && (
                  <div className="px-5 pb-4 pl-12">
                    <p className="font-mono text-sm text-emerald-300/60 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
